/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Floating Link Editor
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { Button } from 'antd';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import {
	NodeSelection,
	LexicalEditor,
	$getSelection,
	$isRangeSelection,
	EditorState,
	RangeSelection,
	SELECTION_CHANGE_COMMAND,
	BaseSelection,
	TextNode,
	ElementNode,
	CLICK_COMMAND,
} from 'lexical';
import { $isAtNodeEnd } from '@lexical/selection';
import { EditOutlined } from '@ant-design/icons';

const getSelectedNode = (selection: RangeSelection): TextNode | ElementNode => {
	const anchor = selection.anchor;
	const focus = selection.focus;
	const anchorNode = selection.anchor.getNode();
	const focusNode = selection.focus.getNode();
	if (anchorNode === focusNode) {
		return anchorNode;
	}
	const isBackward = selection.isBackward();
	if (isBackward) {
		return $isAtNodeEnd(focus) ? anchorNode : focusNode;
	} else {
		return $isAtNodeEnd(anchor) ? anchorNode : focusNode;
	}
};

interface IProps {
	editor: LexicalEditor;
	anchorElem?: HTMLElement;
}

interface IState {
	editorState: EditorState;
}

const LowPriority = 1;
const setFloatingElemPositionForLinkEditor = (domRect: DOMRect | null, editorElem: HTMLElement) => {
	if (domRect === null) {
		editorElem.style.opacity = '0';
		editorElem.style.top = '-1000px';
		editorElem.style.left = '-1000px';
		return;
	}

	const editorWidth = editorElem.offsetWidth || 300;
	const editorHeight = editorElem.offsetHeight || 50;

	// Calculate position relative to the selected text
	let top = domRect.bottom + 10; // 10px gap below text
	let left = domRect.left + domRect.width / 2 - editorWidth / 2; // center horizontally

	// Get viewport dimensions
	const viewportWidth = window.innerWidth;
	const viewportHeight = window.innerHeight;

	// Check if editor would go off the right edge
	if (left + editorWidth > viewportWidth) {
		left = viewportWidth - editorWidth - 10;
	}

	// Check if editor would go off the left edge
	if (left < 10) {
		left = 10;
	}

	// Check if editor would go off the bottom edge
	if (top + editorHeight > viewportHeight) {
		// Position above the selected text instead
		top = domRect.top - editorHeight - 10;
	}

	// Ensure it doesn't go off the top edge when positioned above
	if (top < 10) {
		top = 10;
	}

	editorElem.style.opacity = '1';
	editorElem.style.position = 'fixed';
	editorElem.style.top = `${top}px`;
	editorElem.style.left = `${left}px`;
};

const FloatingLinkEditor: React.FC<IProps> = ({ editor, anchorElem = document.body }) => {
	const editorRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<AnyType>(null);
	const mouseDownRef = useRef(false);
	const [linkUrl, setLinkUrl] = useState('');
	const [editMode, setEditMode] = useState(false);
	const [lastSelection, setLastSelection] = useState<RangeSelection | NodeSelection | BaseSelection | null>(null);

	const updateLinkEditor = useCallback(() => {
		const selection = $getSelection();
		if ($isRangeSelection(selection)) {
			const node = getSelectedNode(selection);
			const parent = node?.getParent();
			if ($isLinkNode(parent)) {
				setLinkUrl(parent.getURL());
			} else if ($isLinkNode(node)) {
				setLinkUrl(node.getURL());
			} else {
				setLinkUrl('');
			}
		}
		const editorElem = editorRef.current;
		const nativeSelection = window.getSelection();
		const activeElement = document.activeElement;

		if (editorElem === null) {
			return;
		}

		const rootElement = editor.getRootElement();
		if (nativeSelection) {
			if (
				selection !== null &&
				!nativeSelection.isCollapsed &&
				rootElement !== null &&
				rootElement.contains(nativeSelection.anchorNode)
			) {
				const domRange = nativeSelection.getRangeAt(0);
				let rect;
				if (nativeSelection.anchorNode === rootElement) {
					let inner: Element = rootElement;
					while (inner.firstElementChild != null) {
						inner = inner.firstElementChild;
					}
					rect = inner.getBoundingClientRect();
				} else {
					rect = domRange.getBoundingClientRect();
				}

				if (!mouseDownRef.current) {
					setFloatingElemPositionForLinkEditor(rect, editorElem);
				}
				setLastSelection(selection);
			} else if (!activeElement || activeElement.className !== 'link-input') {
				setFloatingElemPositionForLinkEditor(null, editorElem);
				setLastSelection(null);
				setEditMode(false);
				setLinkUrl('');
			}
		}

		return true;
	}, [editor, anchorElem]);

	// Handle scroll and resize events like Lexical's implementation
	useEffect(() => {
		const scrollerElem = anchorElem.parentElement;

		const update = () => {
			editor.getEditorState().read(() => {
				updateLinkEditor();
			});
		};

		window.addEventListener('resize', update);

		if (scrollerElem) {
			scrollerElem.addEventListener('scroll', update);
		}

		return () => {
			window.removeEventListener('resize', update);
			if (scrollerElem) {
				scrollerElem.removeEventListener('scroll', update);
			}
		};
	}, [anchorElem.parentElement, editor, updateLinkEditor]);

	useEffect(() => {
		return mergeRegister(
			editor.registerUpdateListener(({ editorState }: IState) => {
				editorState.read(() => {
					updateLinkEditor();
				});
			}),

			editor.registerCommand(
				SELECTION_CHANGE_COMMAND,
				() => {
					updateLinkEditor();
					return true;
				},
				LowPriority,
			),

			editor.registerCommand(
				CLICK_COMMAND,
				(payload) => {
					const selection = $getSelection();
					if ($isRangeSelection(selection)) {
						const node = getSelectedNode(selection);
						const linkNode = $findMatchingParent(node, $isLinkNode);
						if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
							window.open(linkNode.getURL(), '_blank');
							return true;
						}
					}
					return false;
				},
				LowPriority,
			),
		);
	}, [editor, updateLinkEditor]);

	useEffect(() => {
		editor.getEditorState().read(() => {
			updateLinkEditor();
		});
	}, [editor, updateLinkEditor]);

	useEffect(() => {
		if (editMode && inputRef.current) {
			inputRef.current.focus();
		}
	}, [editMode]);

	// // Cleanup effect
	useEffect(() => {
		return () => {
			if (editorRef.current) {
				editorRef.current.style.opacity = '0';
				editorRef.current.style.top = '-1000px';
				editorRef.current.style.left = '-1000px';
			}
		};
	}, []);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			if (lastSelection !== null) {
				if (linkUrl !== '') {
					editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
						url: linkUrl,
						target: '_blank',
						rel: 'noopener noreferrer',
					});
				}
				setEditMode(false);
			}
		} else if (event.key === 'Escape') {
			event.preventDefault();
			setEditMode(false);
		}
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setLinkUrl(event.target.value);
	};

	return (
		<div ref={editorRef} tabIndex={-1} className='link-editor'>
			{editMode ? (
				<input ref={inputRef} className='link-input' value={linkUrl} onChange={handleChange} onKeyDown={handleKeyDown} />
			) : (
				<div className='link-input'>
					<a
						href={linkUrl}
						target='_blank'
						rel='noopener noreferrer'
						onClick={(e) => {
							e.preventDefault();
							window.open(linkUrl, '_blank', 'noopener,noreferrer');
						}}
					>
						{linkUrl}
					</a>
					<Button
						type='text'
						tabIndex={0}
						className='link-edit'
						icon={<EditOutlined />}
						onMouseDown={(e) => e.preventDefault()}
						onClick={() => {
							setEditMode(true);
						}}
					/>
				</div>
			)}
		</div>
	);
};

export default FloatingLinkEditor;
